const chapter = {
  id: 'logarithms',
  slug: 'logarithms',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE',
  title: {
    english: 'Logarithms',
        hindi: 'लघुगणक',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: 'Tool chapter; foundation for many JEE topics',
        hindi: 'टूल अध्याय; कई जेईई विषयों के लिए आधार',
  },
  syllabusTags: ['Algebra', 'JEE Main', 'JEE Advanced'],
  sections: [
    {
      id: 'definition',
      title: {
        english: 'Definition & Bases',
        hindi: 'परिभाषा और आधार',
      },
      blocks: [
        {
          type: 'definition',
          title: {
            english: 'Logarithm',
        hindi: 'लघुगणक',
          },
          body: {
            english: 'For $a > 0,\\ a \\ne 1,\\ N > 0$: $\\log_a N = x \\iff a^x = N$.',
        hindi: '$a > 0,\\ a \\ne 1,\\ N > 0$ के लिए: $\\log_a N = x \\iff a^x = N$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Base restrictions: $a > 0,\\ a \\ne 1$. Argument: $N > 0$.',
        hindi: 'आधार प्रतिबंध: $a > 0,\\ a \\ne 1$. तर्क: $N > 0$.',
            },
            {
              english: '**Common log**: base 10 → $\\log N$ (or $\\log_{10} N$).',
        hindi: '**सामान्य लॉग**: आधार 10 → $\\log N$ (या $\\log_{10} N$)।',
            },
            {
              english: '**Natural log**: base $e$ → $\\ln N$ (or $\\log_e N$); $e \\approx 2.71828$.',
        hindi: '**प्राकृतिक लॉग**: आधार $e$ → $\\ln N$ (या $\\log_e N$); $e \\approx 2.71828$.',
            },
            {
              english: '$\\log_a 1 = 0$, $\\log_a a = 1$, $a^{\\log_a x} = x$ ("inverse property").',
        hindi: '$\\log_a 1 = 0$, $\\log_a a = 1$, $a^{\\log_a x} = x$ ("उलटा गुण")।',
            },
          ],
        },
      ],
    },
    {
      id: 'identities',
      title: {
        english: 'Logarithmic Identities',
        hindi: 'लघुगणकीय सर्वसमिकाएँ',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\log_a (xy) = \\log_a x + \\log_a y',
        },
        {
          type: 'equation',
          equation: '\\log_a \\left( \\dfrac{x}{y} \\right) = \\log_a x - \\log_a y',
        },
        {
          type: 'equation',
          equation: '\\log_a (x^n) = n \\log_a x',
        },
        {
          type: 'equation',
          equation: '\\log_{a^m}(x^n) = \\dfrac{n}{m} \\log_a x',
        },
        {
          type: 'equation',
          equation: '\\log_a x = \\dfrac{\\log_b x}{\\log_b a} = \\dfrac{1}{\\log_x a} \\quad \\text{(change of base)}',
        },
        {
          type: 'equation',
          equation: 'a^{\\log_b c} = c^{\\log_b a}',
        },
        {
          type: 'note',
          body: {
            english: '$\\log_a b \\cdot \\log_b c \\cdot \\log_c a = 1$ (chain product = 1 if cyclic).',
        hindi: '$\\log_a b \\cdot \\log_b c \\cdot \\log_c a = 1$ (श्रृंखला उत्पाद = 1 यदि चक्रीय है)।',
          },
        },
      ],
    },
    {
      id: 'characteristic-mantissa',
      title: {
        english: 'Characteristic, Mantissa & Standard Form',
        hindi: 'अभिलक्षण, मन्टिसा और मानक रूप',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Any +ve real $N$ can be written as $N = m \\cdot 10^c$ where $1 \\le m < 10,\\ c \\in \\mathbb{Z}$.',
        hindi: 'किसी भी वास्तविक $N$ को $N = m \\cdot 10^c$ के रूप में लिखा जा सकता है जहां $1 \\le m < 10,\\\\ c \\in \\mathbb{Z}$ है।',
            },
            {
              english: '$\\log_{10} N = c + \\log_{10} m$ → **integer $c$ = characteristic**, **fractional $\\log_{10} m \\in [0,1)$ = mantissa**.',
        hindi: '$\\log_{10} N = c + \\log_{10} m$ → **पूर्णांक $c$ = विशेषता**, **भिन्नात्मक $\\log_{10} m \\in [0,1)$ = मंटिसा**।',
            },
            {
              english: 'Number of **digits** in $N$ (where $N \\ge 1$) = **characteristic + 1**.',
        hindi: '$N$ (जहाँ $N \\ge 1$) में **अंकों** की संख्या = **विशेषता + 1**।',
            },
            {
              english: 'If $0 < N < 1$: characteristic is **negative** (written as bar e.g. $\\bar{3}.78$); number of zeros immediately after decimal = $|c| - 1$.',
        hindi: 'यदि $0 < N < 1$: विशेषता **नकारात्मक** है (बार के रूप में लिखा गया है जैसे $\\bar{3}.78$); decimal = के तुरंत बाद शून्य की संख्या।',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'How many digits in $2^{50}$?',
        hindi: '$2^{50}$ में कितने अंक हैं?',
          },
          solution: {
            english: '$\\log_{10}(2^{50}) = 50 \\log_{10} 2 \\approx 50 (0.30103) = 15.0515$. Characteristic = 15 → digits = **16**.',
        hindi: '$\\log_{10}(2^{50}) = 50 \\log_{10} 2 \\approx 50 (0.30103) = 15.0515$. विशेषता = 15 → अंक = **16**।',
          },
        },
      ],
    },
    {
      id: 'inequalities',
      title: {
        english: 'Logarithmic Inequalities',
        hindi: 'लघुगणकीय असमिकाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**If $a > 1$**: $\\log_a x_1 > \\log_a x_2 \\iff x_1 > x_2$ (function increasing).',
        hindi: '**यदि $a > 1$**: $\\log_a x_1 > \\log_a x_2 \\iff x_1 > x_2$ (कार्य बढ़ रहा है)।',
            },
            {
              english: '**If $0 < a < 1$**: $\\log_a x_1 > \\log_a x_2 \\iff x_1 < x_2$ (function decreasing — sign flips).',
        hindi: '**यदि $0 < a < 1$**: $\\log_a x_1 > \\log_a x_2 \\iff x_1 < x_2$ (फ़ंक्शन घट रहा है - साइन फ़्लिप)।',
            },
            {
              english: 'Always check $x > 0$ when solving log equations/inequalities.',
        hindi: 'लॉग equations/inequalities को हल करते समय हमेशा $x > 0$ जांचें।',
            },
            {
              english: 'Useful: $\\log_a x > b \\iff x > a^b$ (for $a > 1$); $x < a^b$ (for $0 < a < 1$).',
        hindi: 'उपयोगी: $\\log_a x > b \\iff x > a^b$ ($a > 1$ के लिए); $x < a^b$ ($0 < a < 1$ के लिए)।',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'Solve $\\log_{1/2}(x - 3) > -1$.',
        hindi: '$\\log_{1/2}(x - 3) > -1$ को हल करें।',
          },
          solution: {
            english: 'Domain $x > 3$. Base $1/2 < 1$ → flip: $x - 3 < (1/2)^{-1} = 2$ → $x < 5$. Combined: $\\boxed{3 < x < 5}$.',
        hindi: 'डोमेन $x > 3$. आधार $1/2 <1$ → फ्लिप: $x - 3 < (1/2)^{-1} = 2$ → $x <5$। संयुक्त: $\\boxed{3 < x < 5}$।',
          },
        },
      ],
    },
    {
      id: 'equations',
      title: {
        english: 'Logarithmic Equations',
        hindi: 'लघुगणकीय समीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Strategy: convert to single base, use identities, equate arguments — then **verify in domain**.',
        hindi: 'रणनीति: एकल आधार में परिवर्तित करें, पहचान का उपयोग करें, तर्कों को समान करें - फिर **डोमेन में सत्यापित करें**।',
            },
            {
              english: 'Key forms:',
        hindi: 'मुख्य प्रपत्र:',
            },
            {
              english: '• $\\log_a f(x) = \\log_a g(x) \\Rightarrow f(x) = g(x) > 0$.',
        hindi: '• $\\log_a f(x) = \\log_a g(x) \\Rightarrow f(x) = g(x) > 0$.',
            },
            {
              english: '• $\\log_a f(x) = b \\Rightarrow f(x) = a^b$.',
        hindi: '• $\\log_a f(x) = b \\Rightarrow f(x) = a^b$.',
            },
            {
              english: '• $\\log_{f(x)} g(x) = b \\Rightarrow g(x) = f(x)^b$ with $f(x) > 0,\\ f(x) \\ne 1,\\ g(x) > 0$.',
        hindi: '• $\\log_{f(x)} g(x) = b \\Rightarrow g(x) = f(x)^b$ $f(x) > 0,\\ f(x) \\ne 1,\\ g(x) > 0$ के साथ।',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'Solve $\\log_2(x^2 - 4) = 5$.',
        hindi: '$\\log_2(x^2 - 4) = 5$ को हल करें।',
          },
          solution: {
            english: '$x^2 - 4 = 32 \\Rightarrow x^2 = 36 \\Rightarrow x = \\pm 6$. Both satisfy $x^2 - 4 > 0$. **$x = \\pm 6$**.',
        hindi: '$x^2 ​​- 4 = 32 \\Rightarrow x^2 = 36 \\Rightarrow x = \\pm 6$। दोनों $x^2 - 4 > 0$ को संतुष्ट करते हैं। **$x = \\pm 6$**.',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Solve $(\\log_2 x)^2 - 3 \\log_2 x + 2 = 0$.',
        hindi: '$(\\log_2 x)^2 - 3 \\log_2 x + 2 = 0$ को हल करें।',
          },
          solution: {
            english: 'Let $t = \\log_2 x$. $t^2 - 3t + 2 = 0 \\Rightarrow t = 1, 2 \\Rightarrow x = 2, 4$.',
        hindi: 'मान लीजिए $t = \\log_2 x$. $t^2 - 3t + 2 = 0 \\Rightarrow t = 1, 2 \\Rightarrow x = 2, 4$।',
          },
        },
      ],
    },
    {
      id: 'misc',
      title: {
        english: 'Useful Tricks for JEE',
        hindi: 'JEE के लिए उपयोगी युक्तियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$\\log_a b = \\dfrac{1}{\\log_b a}$ → reciprocal.',
        hindi: '$\\log_a b = \\dfrac{1}{\\log_b a}$ → पारस्परिक.',
            },
            {
              english: '$\\log_{a^n} b^n = \\log_a b$ (same base & power → cancels).',
        hindi: '$\\log_{a^n} b^n = \\log_a b$ (समान आधार और शक्ति → रद्दीकरण)।',
            },
            {
              english: 'For comparison of logs with different bases, convert all to natural log: $\\log_a b = \\dfrac{\\ln b}{\\ln a}$.',
        hindi: 'विभिन्न आधारों वाले लॉग की तुलना के लिए, सभी को प्राकृतिक लॉग में बदलें: $\\log_a b = \\dfrac{\\ln b}{\\ln a}$।',
            },
            {
              english: '$y = \\log_a x$ has domain $(0, \\infty)$, range $\\mathbb{R}$, passes through $(1, 0)$ and $(a, 1)$.',
        hindi: '$y = \\log_a x$ का डोमेन $(0, \\infty)$ है, रेंज $\\mathbb{R}$, $(1, 0)$ और $(a, 1)$ से होकर गुजरता है।',
            },
            {
              english: 'Graph of $\\log_a x$ for $a > 1$ is increasing & concave down; for $0 < a < 1$ → decreasing & concave up.',
        hindi: '$a > 1$ के लिए $\\log_a x$ का ग्राफ़ बढ़ रहा है और नीचे की ओर अवतल हो रहा है; $0 < a < 1$ के लिए → घटता और अवतल होता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\log_a (xy) = \\log_a x + \\log_a y',
      desc: {
        english: 'Product',
        hindi: 'उत्पाद',
      },
    },
    {
      latex: '\\log_a (x/y) = \\log_a x - \\log_a y',
      desc: {
        english: 'Quotient',
        hindi: 'भागफल',
      },
    },
    {
      latex: '\\log_a x^n = n \\log_a x',
      desc: {
        english: 'Power',
        hindi: 'शक्ति',
      },
    },
    {
      latex: '\\log_a x = \\dfrac{\\log_b x}{\\log_b a}',
      desc: {
        english: 'Change of base',
        hindi: 'आधार परिवर्तन',
      },
    },
    {
      latex: 'a^{\\log_a x} = x',
      desc: {
        english: 'Inverse',
        hindi: 'उलटा',
      },
    },
    {
      latex: 'a^{\\log_b c} = c^{\\log_b a}',
      desc: {
        english: 'Swap',
        hindi: 'स्वैप',
      },
    },
    {
      latex: '\\log_a b \\cdot \\log_b a = 1',
      desc: {
        english: 'Reciprocal',
        hindi: 'पारस्परिक',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Identities',
      problem: {
        english: 'If $\\log_2 x + \\log_4 x + \\log_{16} x = 7$, then $x = ?$',
        hindi: 'यदि $\\log_2 x + \\log_4 x + \\log_{16} x = 7$, तो $x =?$',
      },
      answer: {
        english: '$x = 16$',
        hindi: '$x = 16$',
      },
      solution: {
        english: '$\\log_2 x + \\frac{1}{2}\\log_2 x + \\frac{1}{4}\\log_2 x = 7 \\Rightarrow \\frac{7}{4}\\log_2 x = 7 \\Rightarrow \\log_2 x = 4 \\Rightarrow x = 16$.',
        hindi: '$\\log_2 x + \\frac{1}{2}\\log_2 x + \\frac{1}{4}\\log_2 x = 7 \\Rightarrow \\frac{7}{4}\\log_2 x = 7 \\Rightarrow \\log_2 x = 4 \\Rightarrow x = 16$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Inequality',
      problem: {
        english: 'Solve $\\log_{0.5}(2x - 1) > 0$.',
        hindi: '$\\log_{0.5}(2x - 1) > 0$ को हल करें।',
      },
      answer: {
        english: '$1/2 < x < 1$',
        hindi: '$1/2 < x < 1$ बेस < 1 → फ्लिप: $1/2 < x < 1$ और → ।',
      },
      solution: {
        english: 'Base < 1 → flip: $2x - 1 < 1$ and $2x - 1 > 0$ → $1/2 < x < 1$.',
        hindi: 'आधार <1 → फ्लिप: $2x - 1 <1$ और $2x - 1 > 0$ → $1/2 <x <1$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Digits',
      problem: {
        english: 'Number of digits in $5^{100}$ (use $\\log_{10}5 = 0.699$).',
        hindi: '$5^{100}$ में अंकों की संख्या ($\\log_{10}5 = 0.699$ का उपयोग करें)।',
      },
      answer: {
        english: '70',
        hindi: '70',
      },
      solution: {
        english: '$100 \\times 0.699 = 69.9$ → characteristic 69 → 70 digits.',
        hindi: '$100 \\times 0.699 = 69.9$ → विशेषता 69 → 70 अंक।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Adv',
      tag: 'Equation',
      problem: {
        english: 'Number of real solutions of $\\log_{x+1}(x - 0.5) = \\log_{x - 0.5}(x + 1)$.',
        hindi: '$\\log_{x+1}(x - 0.5) = \\log_{x - 0.5}(x + 1)$ के वास्तविक समाधानों की संख्या।',
      },
      answer: {
        english: '1',
        hindi: '1',
      },
      solution: {
        english: '$\\log_a b = \\log_b a \\Rightarrow (\\log_a b)^2 = 1 \\Rightarrow b = a$ or $b = 1/a$. Solve with domains.',
        hindi: '$\\log_a b = \\log_b a \\Rightarrow (\\log_a b)^2 = 1 \\Rightarrow b = a$ या $b = 1/a$. डोमेन के साथ हल करें.',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'Identity',
      problem: {
        english: 'Value of $\\log_3 4 \\cdot \\log_4 5 \\cdot \\log_5 6 \\cdot \\log_6 7 \\cdot \\log_7 8$.',
        hindi: '$\\log_3 4 \\cdot \\log_4 5 \\cdot \\log_5 6 \\cdot \\log_6 7 \\cdot \\log_7 8$ का मान.',
      },
      answer: {
        english: '$\\log_3 8 = 3\\log_3 2$',
        hindi: '$\\log_3 8 = 3\\log_3 2$',
      },
      solution: {
        english: 'Telescoping: $\\dfrac{\\ln 4}{\\ln 3} \\cdot \\dfrac{\\ln 5}{\\ln 4} \\cdots \\dfrac{\\ln 8}{\\ln 7} = \\dfrac{\\ln 8}{\\ln 3}$.',
        hindi: 'टेलीस्कोपिंग: $\\dfrac{\\ln 4}{\\ln 3} \\cdot \\dfrac{\\ln 5}{\\ln 4} \\cdots \\dfrac{\\ln 8}{\\ln 7} = \\dfrac{\\ln 8}{\\ln 3}$.',
      },
    },
  ],
  next: {
    slug: 'inverse-trigonometric-functions',
    title: 'Inverse Trigonometric Functions',
  },
};

export default chapter;
